import config from '../config';
import { devLog } from '../utils/devLog';

const AUTH_FAILED_EVENT = 'ffb-auth-failed';
const STORAGE_ACCESS = 'ffb_access_token';
const STORAGE_HAD_SESSION = 'ffb_had_session';

let fetchInFlight = null;
let sessionKnownDead = false;
/** In-memory access JWT when cross-origin cookies are blocked. */
let memoryAccessToken = null;

/** Read the current access token (memory + sessionStorage) for WebSocket auth. */
export function getAccessTokenForWebSocket() {
  return readStoredAccessToken();
}

function readStoredAccessToken() {
  if (memoryAccessToken) {
    return memoryAccessToken;
  }
  try {
    const stored = sessionStorage.getItem(STORAGE_ACCESS);
    if (stored) {
      memoryAccessToken = stored;
    }
    return stored;
  } catch {
    return null;
  }
}

function writeStoredAccessToken(token) {
  memoryAccessToken = token || null;
  try {
    if (token) {
      sessionStorage.setItem(STORAGE_ACCESS, token);
    } else {
      sessionStorage.removeItem(STORAGE_ACCESS);
    }
  } catch {
    /* ignore private mode / blocked storage */
  }
}

function markHadSession() {
  try {
    sessionStorage.setItem(STORAGE_HAD_SESSION, '1');
  } catch {
    /* ignore */
  }
}

function clearHadSession() {
  try {
    sessionStorage.removeItem(STORAGE_HAD_SESSION);
  } catch {
    /* ignore */
  }
}

/** True when this tab has previously established a login (skip pointless refresh on first visit). */
function hadSessionInThisTab() {
  return Boolean(readStoredAccessToken()) || sessionStorage.getItem(STORAGE_HAD_SESSION) === '1';
}

/** Broadcast when refresh cookies are invalid so the app can stop WS/polling loops. */
export function notifyAuthSessionInvalid() {
  sessionKnownDead = true;
  writeStoredAccessToken(null);
  clearHadSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_FAILED_EVENT));
  }
}

/** Call after a successful login so we retry /users/me once. */
export function clearAuthSessionDead() {
  sessionKnownDead = false;
}

export function isAuthSessionDead() {
  return sessionKnownDead;
}

export function onAuthSessionInvalid(handler) {
  if (typeof window === 'undefined') {
    return () => {};
  }
  window.addEventListener(AUTH_FAILED_EVENT, handler);
  return () => window.removeEventListener(AUTH_FAILED_EVENT, handler);
}

/** Store access token from login/refresh JSON when cookies cannot be used cross-origin. */
export function applyAccessTokenFromAuthResponse(data) {
  if (data?.access_token) {
    writeStoredAccessToken(data.access_token);
    markHadSession();
    sessionKnownDead = false;
  }
}

export function clearMemoryAccessToken() {
  writeStoredAccessToken(null);
  clearHadSession();
}

/** Merge Authorization + credentials for credentialed API calls. */
export function authFetchInit(init = {}) {
  readStoredAccessToken();
  const headers = new Headers(init.headers || {});
  if (memoryAccessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${memoryAccessToken}`);
  }
  return {
    ...init,
    headers,
    credentials: init.credentials ?? 'include',
  };
}

/**
 * Restore the logged-in user from cookies and/or Bearer token.
 * On a first visit (no prior login in this tab), a single 401 on /users/me is expected
 * and we do not call /users/refresh.
 */
export async function fetchCurrentUser() {
  if (sessionKnownDead) {
    return null;
  }

  if (fetchInFlight) {
    return fetchInFlight;
  }

  fetchInFlight = (async () => {
    readStoredAccessToken();

    const meResponse = await fetch(
      `${config.API_URL}/users/me`,
      authFetchInit({ method: 'GET' })
    );

    if (meResponse.ok) {
      sessionKnownDead = false;
      markHadSession();
      return meResponse.json();
    }

    if (meResponse.status !== 401) {
      devLog('fetchCurrentUser: /users/me failed', meResponse.status);
      return null;
    }

    // First visit to the site in this tab: not logged in — one 401 is normal; do not refresh.
    if (!hadSessionInThisTab()) {
      devLog('fetchCurrentUser: no session (guest)');
      return null;
    }

    devLog('fetchCurrentUser: access token expired, trying refresh');
    const refreshResponse = await fetch(
      `${config.API_URL}/users/refresh`,
      authFetchInit({ method: 'POST' })
    );

    if (!refreshResponse.ok) {
      devLog('fetchCurrentUser: refresh failed', refreshResponse.status);
      notifyAuthSessionInvalid();
      return null;
    }

    try {
      const refreshData = await refreshResponse.json();
      applyAccessTokenFromAuthResponse(refreshData);
    } catch {
      // refresh body optional
    }

    const retryResponse = await fetch(
      `${config.API_URL}/users/me`,
      authFetchInit({ method: 'GET' })
    );

    if (retryResponse.ok) {
      sessionKnownDead = false;
      markHadSession();
      return retryResponse.json();
    }

    devLog('fetchCurrentUser: /users/me after refresh failed', retryResponse.status);
    notifyAuthSessionInvalid();
    return null;
  })();

  try {
    return await fetchInFlight;
  } finally {
    fetchInFlight = null;
  }
}

/** After login POST succeeds, confirm session works before treating user as signed in. */
export async function verifySessionAfterLogin() {
  clearAuthSessionDead();
  markHadSession();
  return fetchCurrentUser();
}
